import { getLocalStorageItem } from 'hooks';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAPI } from 'services/axios';
import formatUrlParams from 'utils/formatUrlParams';

import { Endpoints, queryKeys } from '../constants';

export const appendQueryParams = (
  url: string,
  data: Record<string, any>
): string => {
  let resp = `${url}?`;
  if (data) {
    Object.keys(data).forEach((key) => {
      if (data[key] || typeof data[key] === 'boolean') {
        resp += `${key}=${
          typeof data[key] === 'string'
            ? data[key]?.replaceAll('&', '%26')
            : data[key]
        }&`;
      }
    });
  }
  return resp;
};

//  Designation Listing Api //
const designationListApi = () => {
  return useAPI({ method: 'GET', url: Endpoints.USER_DESIGNATION });
};

export const useDesignationListApi = () => {
  return useQuery([queryKeys.USER_DESIGNATION], designationListApi, {});
};

// Create User APi//
const userCreateApi = (payload: any) => {
  return useAPI({
    method: 'POST',
    url: Endpoints.CREATE_USER,
    data: payload,
  });
};

export const useUserCreateApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(userCreateApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.USER_LIST]);
      // queryClient.setQueryData([QueryKeys.AUTH_ME], response?.data?.user);
      queryClient.invalidateQueries([queryKeys.AUTH_ME]);
    },
  });
};

// Username  Availability  APi//
const userAvailabilityApi = (payload: any) => {
  return useAPI({
    method: 'POST',
    url: Endpoints.USERNAME_AVAILABLE,
    data: payload,
  });
};

export const useUserAvailabilityApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(userAvailabilityApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.AUTH_ME]);
    },
  });
};
const userPauseAvailabilityApi = (uuid: any) => {
  return useAPI({
    method: 'GET',
    url: `users/${uuid}/schedules/pause`,
  });
};

export const userPauseAvailabilityApiQuery = (uuid: any) => {
  return useQuery(
    [queryKeys.USER_PAUSE_AVAILABILITY],
    () => userPauseAvailabilityApi(uuid),
    {
      enabled: true,
    }
  );
};

// Username  Availability  APi//
const pauseAvailabilityApi = (payload: any) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'POST',
    url: `users/${uuid}/schedules/pause`,
    data,
  });
};

export const usepauseAvailabilityApiQuery = () => {
  return useMutation(pauseAvailabilityApi);
};

const deletePauseAvailabilityApi = (payload: any) => {
  const { data } = payload;
  return useAPI({
    method: 'DELETE',
    url: `users/${payload?.uuid}/schedules/pause/${data?.id}`,
    data,
  });
};

export const useDeletePauseAvailabilityApiQuery = () => {
  return useMutation(deletePauseAvailabilityApi);
};

const readNotificationApi = (payload: any) => {
  return useAPI({
    method: 'PATCH',
    url: `notifications/${payload?.notification_id}/mark-read`,
  });
};

export const useReadNotificationApiQuery = () => {
  return useMutation(readNotificationApi);
};

const readAllNotificationApi = () => {
  return useAPI({
    method: 'PATCH',
    url: `notifications/mark-read/all`,
  });
};

export const useReadAllNotificationApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(readAllNotificationApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.NOTIFICATION_LIST]);
    },
  });
};

const resetAvailabilityApi = () => {
  const userDetails = getLocalStorageItem('roles');
  const data1 = {
    userUuid: userDetails?.uuid,
  };
  return useAPI({
    method: 'DELETE',
    url: `users/${data1?.userUuid}/schedules`,
  });
};

export const useResetAvailabilityApiQuery = () => {
  return useMutation(resetAvailabilityApi);
};
const SaveFCMToken = (payload: any) => {
  const deviceFingerprint = getLocalStorageItem('deviceFingerprint');
  const payloadData = {
    // userId: userDetails?.id,
    token: payload?.fcmToken,
    type: 'WEB',
  };
  return useAPI({
    method: 'PATCH',
    url: `device/${deviceFingerprint}/token`,
    data: payloadData,
  });
};

export const SaveFCMTokenMutation = () => {
  return useMutation(SaveFCMToken);
};

// User table listing Api //
const userTableListApi = (data: any) => {
  const { userListFilters, userParams } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`${Endpoints.CREATE_USER}`, queryParams),
  });
};

export const useUserTableListApiQuery = (data: any) => {
  return useQuery([queryKeys.USER_LIST, data], () => userTableListApi(data), {
    cacheTime: 0,
    enabled: true,
  });
};

// User blast group listing Api //
const userBlastGroupListApi = (accountId: any) => {
  return useAPI({
    method: 'GET',
    url: `/groups/has-blast?accountId=${accountId}`,
  });
};

export const useUserBlastGroupListApiQuery = (accountId: any) => {
  return useQuery(
    [queryKeys.USER_LIST, accountId],
    () => userBlastGroupListApi(accountId),
    {
      cacheTime: 0,
      enabled: true,
    }
  );
};

// notifcation list APi
const userNotificationListApi = () => {
  return useAPI({
    method: 'GET',
    url: `notifications?page=1&pageSize=500&bring=unread_only`,
  });
};

export const useNotificationListApiQuery = () => {
  return useQuery(
    [queryKeys.NOTIFICATION_LIST],
    () => userNotificationListApi(),
    {
      enabled: true,
      cacheTime: 0,
    }
  );
};

// Update user api //
const updateUserApi = (payload: any) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `${Endpoints.CREATE_USER}/${uuid}`,
    data,
  });
};

export const useUpdateUserApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(updateUserApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.USER_LIST]);
    },
  });
};

// Change status of  user api //
const changeUserStatusApi = (payload: any) => {
  return useAPI({
    method: 'PATCH',
    url: `${Endpoints.CREATE_USER}/${payload?.uuid}/change-status`,
    params: formatUrlParams(payload.data),
  });
};

export const useChangeUserStatusApiQuery = (data: any) => {
  const queryClient = useQueryClient();
  return useMutation(() => changeUserStatusApi(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeys.USER_LIST,
        data.status,
        data.uuid,
      ]);
    },
  });
};

// Get user details through Id //
const userDetailsApi = (data: any) => {
  return useAPI({
    method: 'GET',
    url: `${Endpoints.CREATE_USER}/${data?.uuid}`,
  });
};

export const useUserDetailsApiQuery = () => {
  return useQuery([queryKeys.USER_LIST], userDetailsApi, {
    enabled: true,
  });
};

// Get Autocomplete users //
const userAutocompleteApi = (data: any) => {
  return useAPI({
    method: 'GET',
    url: `${Endpoints.USER_AUTOCOMPLETE}`,
    params: formatUrlParams(data),
  });
};

export const useUserAutocompleteApiQuery = (data: any) => {
  return useQuery(
    [queryKeys.USER_AUTOCOMPLETE, data.query],
    () => userAutocompleteApi(data),
    {
      enabled: true,
    }
  );
};

// Fetch Unread Messages
const userUnreadMessageCount = () => {
  return useAPI({
    method: 'GET',
    url: Endpoints.UNREAD_MESSAGES_COUNT,
  });
};

export const useUserUnreadMessageCount = () => {
  return useQuery(
    [queryKeys.UNREAD_MESSAGES_COUNT],
    () => userUnreadMessageCount(),
    { refetchInterval: !!localStorage.getItem('AUTH_TOKEN') && 15000 }
  );
};

// post userFeedback
const userFeedback = (payload: any) => {
  const { userId, message, pageLocation, file } = payload;
  const formData = new FormData();

  formData.append('message', message);
  formData.append('userId', userId);
  formData.append('pageLocation', pageLocation);

  if (file) {
    formData.append('files', file);
  }
  return useAPI({
    method: 'POST',
    url: `/messages/feedback`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  });
};

export const useUserFeedback = () => {
  return useMutation(userFeedback, {
    onSuccess: (data, variables) => {
      // Optional: Handle global success here
    },
    onError: (error) => {
      // Optional: Handle global errors here
    },
  });
};
